import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToParentElement } from '@dnd-kit/modifiers'

import { Box, Button, Stack } from '@mui/material'
import { PlusIcon } from 'shared/icons'
import Grid from '@mui/material/Grid'

import { Loading } from 'components/controls/loading'
import { BlockCaption } from 'components/controls/blockCaption'

import { useRemoveSearchsDrug, useListSearchsDrug, useSortSearchsDrug } from 'interface/search/drug'

import { useSortableList } from '../container/useSortableList'
import { useSortableListEdit } from '../container/useSortableListEdit'
import { EdittableDataListRecord, StaticDataListRecord } from '../components/record'

import { EditSearchsDrugDialog } from './dialog/edit'
import { AddSearchsDrugDialog } from './dialog/add'
import { ErrorInfo } from 'components/controls/errorInfo'

const SearchsDrugCaption = "対象薬剤"

const gridRules = {
  size: {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 4,
    xl: 3
  }
}

type EdittableSearchsDrugListBlockProps = {
  searchId: number
}
const EdittableSearchsDrugListBlock = ({
  searchId
}: EdittableSearchsDrugListBlockProps) => {
  const { loading, data, error } = useListSearchsDrug(searchId)
  const list = useSortableList({ list: data?.list ?? [] })
  const { sortableList } = list
  const [postState, post] = useSortSearchsDrug(searchId)
  const [removeState, remove] = useRemoveSearchsDrug(searchId)
  const {
		modal, closeModal,
		onAdd, onEditIt, onRemoveIt,
		onSubmitEdit, onSubmitAdd,
		onDragEnd
  } = useSortableListEdit({
    ...list,
    post: (data) => post(data?.map(r => r.searchsDrugId) ?? [])
  })
  return (
    <Stack direction="column" position="relative">
      <Stack direction="row" spacing={1} alignItems="flex-end" width="100%" >
        <Box flexGrow={1}><BlockCaption flexGrow={1} caption={SearchsDrugCaption}/></Box>
        <Button
          startIcon={<PlusIcon />}
          variant="contained"
          size="small"
          sx={{ width: 'auto' }}
          onClick={onAdd}
        >
          追加
        </Button>
      </Stack>
      {error && <ErrorInfo error={error}/>}
      {(loading || postState.posting || removeState.posting) && <Loading/>}
      {(postState.posting || removeState.posting) && <Loading />}

      {sortableList.length > 0 && (
        <Grid container flexGrow={1} spacing={1} marginTop={1}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableList}>
              {sortableList.map((drug, i) => (
                <EdittableDataListRecord
                  key={`drug-${i}`}
                  data={drug}
                  displayName={drug.drugName ?? 'No name'}
                  onEditIt={() => {
                    onEditIt(drug)
                  }}
                  onRemoveIt={() => {
                    remove(drug.searchsDrugId)
                    onRemoveIt(drug)
                  }}
                  {...gridRules}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Grid>
      )}

      { modal?.command === 'add' ? (
        <AddSearchsDrugDialog
          searchId={searchId}
          onSubmit={onSubmitAdd}
          onClose={closeModal}
        />
      ) : (modal?.command === 'edit' && modal.data) ? (
        <EditSearchsDrugDialog
          searchId={searchId}
          drug={modal.data}
          onSubmit={onSubmitEdit}
          onClose={closeModal}
        />
      ) : (
        <></>
      )}
    </Stack>
  )
}

type StaticSearchsDrugListBlockProps = {
  searchId: number
}
const StaticSearchsDrugListBlock = ({
  searchId
}: StaticSearchsDrugListBlockProps) => {
  const { loading, data, error } = useListSearchsDrug(searchId)
  if (loading || !data || data.list.length === 0) return <></>
  return (
    <Stack direction="column" position="relative">
      <BlockCaption caption={SearchsDrugCaption} />
      {error && <ErrorInfo error={error}/>}

      <Grid container flexGrow={1} spacing={1} marginTop={0.5}>
        {data.list.map((drug, i) => (
          <StaticDataListRecord
            key={`drug-${i}`}
            displayName={drug.drugName ?? 'No name'}
            {...gridRules}
          />
        ))}
      </Grid>
    </Stack>
  )
}

type SearchsDrugListBlockProps = {
  edittable: boolean
  searchId: number
}
export const SearchsDrugListBlock = ({
  edittable,
  searchId
}: SearchsDrugListBlockProps) => edittable ? (
  <EdittableSearchsDrugListBlock
    searchId={searchId}
  />
) : (
  <StaticSearchsDrugListBlock
    searchId={searchId}
  />
)